import EventEmitter from 'events'
import { GetToken, Logout } from '../api'
import * as Sentry from '@sentry/react'
import { withForms } from '@twind/forms'
import { Styles } from '../utils/Styles'
import { theme } from '../tailwind.config'
import { FiresquadWidget } from './Widget'
import typography from '@twind/typography'
import { lineClamp } from '@twind/line-clamp'
import { ArgParser } from '../utils/ArgsParser'
import { createApolloClient } from '../api/Client'
import { css, ThemeConfiguration } from 'twind/css'
import { createRoot, Root } from 'react-dom/client'
import * as SetUser from './Actions/SetUser.generated'
import { EventEmitterProvider } from '../utils/EventEmitter'
import { ApolloClient, ApolloProvider } from '@apollo/client'
import * as AuthUser from './Actions/AuthenticateUser.generated'
import * as SetUserAttrs from './Actions/SetUserAttributes.generated'

let container: HTMLDivElement | null = null

Sentry.init({
  release: process.env.RELEASE,
  dsn: process.env.SENTRY_DSN,
  enabled: !!process.env.SENTRY_DSN,
  tracesSampleRate: 1.0,
  defaultIntegrations: false,
})

type FiresquadOptions = {
  /**
   * If firesquad is self-hosted, use this alternate endpoint for connection.
   */
  endpoint?: string

  /**
   * If firesquad is self-hosted, use this alternate WS endpoint for connection.
   */
  wsEndpoint?: string

  /**
   * If you want to have the widget on the left side, use this.
   */

  left?: boolean | 'true' | 'false'

  /**
   * If your site is not responsive or doesn't handle mobile devices, set this to false.
   */
  responsive?: boolean | 'hide' | 'true' | 'false'

  /**
   * A custom media query breakpoint to be used mobile device detection.
   */
  responsiveBreakpoint?: number | 'string'
}
class Firesquad {
  /** Assigned appId */
  private __app_id: string

  /** Client */
  private __client: ApolloClient<unknown> | null = null

  /** True when the app profile has been loaded */
  private __ready = false

  /** True when the websocket connection has been adquired */
  private __wsReady = false

  /** Main endpoint */
  private __endpoint: string

  /** Event emitter instance */
  private eventEmitter: EventEmitter

  /** Token to use for authenticate the app */
  private __token: { token: string; expires: number } | null = null

  private root: Root

  /**
   * Returns a boolean indicating if the widget is ready to be used.
   * @returns {Boolean} true if the widget is ready to be used.
   */
  static initialized() {
    return container !== null
  }

  /**
   * If the widget is ready (Profile loaded and websocket running) this is true.
   */
  get ready() {
    return this.__ready && this.__wsReady
  }

  /**
   * Initializes the widget.
   * @param {string} appId - The app id to initialize this widget.
   */
  constructor(appId: string, options?: FiresquadOptions) {
    this.__app_id = appId
    this.__endpoint = options?.endpoint || process.env.ENDPOINT
    this.eventEmitter = new EventEmitter()

    const responsive =
      options?.responsive === 'hide'
        ? 'hide'
        : ArgParser.parseBool(options?.responsive, true)

    const responsiveBreakpoint = ArgParser.parseUInt(
      options?.responsiveBreakpoint,
      400
    )

    /** We'll need to check for ready event */
    this.eventEmitter.once('____ws_ready', () => {
      this.__wsReady = true
      if (this.ready) this.eventEmitter.emit('ready')
    })

    this.eventEmitter.once('____app_ready', () => {
      this.__ready = true
      if (this.ready) this.eventEmitter.emit('ready')
    })

    /** If the widget failed, will restart in ~150ms */
    this.eventEmitter.on('____app_restart', async () => {
      await this.dispose()

      setTimeout(() => {
        new Firesquad(appId, options)
      })
    })

    if (container) {
      throw new Error('Firesquad has already been booted.')
    }

    container = document.createElement('div')
    const shadowRoot =
      'attachShadow' in container
        ? container.attachShadow({ mode: 'closed' })
        : container

    const appContainer = document.createElement('div')
    const hostStyle = document.createElement('style')
    const pageStyle = document.createElement('style')

    hostStyle.textContent = `
      :host {
        display: block;
      }
      `
    pageStyle.textContent = `
      body.fs-widget {
        overflow: hidden;
        width: 100vw;
        height: 100%;
        overflow: hidden;
        position: fixed;
        background-color: rgba(249,250,251,var(--tw-bg-opacity));
        top: 0;
        right: 0;
      }
      `

    shadowRoot.appendChild(appContainer)
    shadowRoot.appendChild(hostStyle)
    document.body.appendChild(container)
    document.head.appendChild(pageStyle)

    this.__client = createApolloClient(
      this.eventEmitter,
      this.__endpoint,
      options?.wsEndpoint || process.env.WS_ENDPOINT,
      () => this.getToken()
    )

    this.root = createRoot(appContainer)
    this.getToken().then(() => {
      this.root.render(
        <Sentry.Profiler>
          <EventEmitterProvider eventEmitter={this.eventEmitter}>
            <ApolloProvider client={this.__client}>
              <Styles
                sheet={hostStyle}
                settings={{
                  theme: theme as unknown as ThemeConfiguration,
                  hash: process.env.NODE_ENV === 'production',
                  prefix: true,
                  mode: 'strict',
                  preflight: withForms((preflight) => {
                    preflight['@import'] = [
                      `url(https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap)`,
                      `url(https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&display=swap)`,
                    ]

                    return css(preflight, {
                      '*': {
                        'font-family': '"DM Sans", sans-serif',
                      },
                    })
                  }),
                  plugins: {
                    ...typography(),
                    'line-clamp': lineClamp,
                  },
                }}
              >
                <Sentry.ErrorBoundary
                  fallback={null}
                  onError={() => {
                    window.document.body.classList.remove('fs-widget')
                    setTimeout(() => {
                      this.eventEmitter.emit('____app_restart')
                    }, 300)
                  }}
                >
                  <FiresquadWidget
                    left={ArgParser.parseBool(options?.left, false)}
                    root={shadowRoot}
                    responsive={responsive}
                    responsiveBreakpoint={responsiveBreakpoint}
                    openCallback={(open) => {
                      if (responsive) {
                        const body = window.document.body
                        const isMobile = window.matchMedia(
                          `(max-width: ${responsiveBreakpoint}px)`
                        )

                        if (isMobile.matches) {
                          return open
                            ? body.classList.add('fs-widget')
                            : body.classList.remove('fs-widget')
                        }
                      }
                    }}
                  />
                </Sentry.ErrorBoundary>
              </Styles>
            </ApolloProvider>
          </EventEmitterProvider>
        </Sentry.Profiler>
      )
    })
  }

  private async getToken() {
    if (this.__token && this.__token.token)
      return `Bearer ${this.__token.token}`

    this.__token = await GetToken(this.__endpoint, this.__app_id)

    return `Bearer ${this.__token.token}`
  }

  logout() {
    /**
     * We will restart after
     */
    Logout(this.__endpoint, this.__app_id).then(() =>
      this.eventEmitter.emit('____app_restart')
    )
  }

  /**
   * Methods calls will need to wait for the widget to be set so we receive a proper behavior.
   * @returns {Promise<true>} It's ready
   */
  private readyListener(): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.ready) resolve(true)

      this.once('ready', () => resolve(true))
    })
  }

  /**
   * Adds the `listener` function to the end of the listeners array for the
   * event named `eventName`.
   * @param eventName - Event name.
   * @param listener - Listener callback.
   * @returns Event emitter reference.
   */
  on<T extends unknown[]>(eventName: string, listener: (...args: T) => void) {
    return this.eventEmitter.on(eventName, listener)
  }

  /**
   * Removes the specified `listener` from the listener array for the event named`eventName`.
   * @param eventName - Event name.
   * @param listener - Listener callback.
   * @returns Event emitter reference.
   */
  off<T extends unknown[]>(eventName: string, listener: (...args: T) => void) {
    return this.eventEmitter.off(eventName, listener)
  }

  /**
   * Adds a **one-time**`listener` function for the event named `eventName`. The
   * next time `eventName` is triggered, this listener is removed and then invoked.
   * @param eventName - Event name.
   * @param listener - Listener callback.
   * @returns Event emitter reference.
   */
  once<T extends unknown[]>(eventName: string, listener: (...args: T) => void) {
    return this.eventEmitter.once(eventName, listener)
  }

  /**
   * Set data about the current widget visitor.
   * @param user - User object to set.
   * @returns Result operation.
   */
  async SetUser(user: SetUser.SetUserMutationVariables['data']) {
    await this.readyListener()
    return this.__client.mutate<SetUser.SetUserMutation>({
      mutation: SetUser.SetUserDocument,
      variables: { data: user },
    })
  }

  /**
   * Set attributes (or data points) about the current widget visitor.
   * @param attrs - Attributes to set.
   * @returns Result operation.
   */
  async SetUserAttributes(
    attrs: SetUserAttrs.SetUserAttributesMutationVariables['data']
  ) {
    await this.readyListener()
    return this.__client.mutate<SetUserAttrs.SetUserAttributesMutation>({
      mutation: SetUserAttrs.SetUserAttributesDocument,
      variables: { data: attrs },
    })
  }

  /**
   * Starts the authentication procedure against a secure backend.
   * @param input - Authentication data, only `token` is required, attributes are optional.
   * @returns Result operation.
   */
  async AuthenticateUser(
    input: AuthUser.AuthenticateUserMutationVariables['data']
  ) {
    await this.readyListener()
    return this.__client.mutate<AuthUser.AuthenticateUserMutation>({
      mutation: AuthUser.AuthenticateUserDocument,
      variables: { data: input },
    })
  }

  open() {
    this.eventEmitter.emit('__widget_open')
  }

  /**
   * Closes and disposes the widget.
   */
  async dispose() {
    try {
      await this.__client?.resetStore()
    } finally {
      this.__client = null
      this.__token = null

      if (this.root) this.root.unmount()

      if (container) {
        document.body.removeChild(container)
        container = null
      }
    }
  }
}

export default Firesquad

const script =
  (document?.currentScript as HTMLScriptElement) ||
  document.querySelector<HTMLScriptElement>('[src*="firesquad"]')

function runFromScript() {
  const { key, ...params } = Object.fromEntries(
    new URL(script.src).searchParams.entries()
  )

  new Firesquad(key, params)
}

if (script && new URL(script.src).searchParams.has('key')) {
  if (!document.body) {
    window.addEventListener('load', () => {
      runFromScript()
    })
  } else {
    runFromScript()
  }
}
